import { Box, capitalize } from "@mui/material"
import { type OnChangeFn, type SortingState, getCoreRowModel } from "@tanstack/react-table"
import type { FC } from "react"
import React, { useRef } from "react"

import { CancelDialog } from "@/components/common/Modals"
import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { SignedUrl } from "@/graphql/codegen/graphql"
import useCompanyFilesColumns from "@/screens/Companies/edit/components/info/hooks/useCompanyFilesColumns"
import useFileStatusChange from "@/screens/Companies/edit/components/info/hooks/useFileStatusChange"

const CompanyFilesTable: FC<{
  data: SignedUrl[]
  sorting: SortingState
  handleSortingChange: (updaterFn: (state: SortingState) => SortingState) => void
}> = ({ data, sorting, handleSortingChange }) => {
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const {
    isChangeStatusDialogOpen,
    handleFileStatusChangeConfirmationDialogOpen,
    onCloseConfirmationDialog,
    isChangingStatus,
    handleFileStatusChange,
    nextFileStatusAction,
  } = useFileStatusChange()

  const columns = useCompanyFilesColumns({
    isChangingStatus,
    handleFileStatusChangeConfirmationDialogOpen,
    nextFileStatusAction,
  })

  return (
    <Box className='flex h-full flex-col'>
      <AerosBaseTable
        features={{
          table: {
            stickyHeader: true,
          },
          tanstackOptions: {
            data,
            columns,
            state: {
              sorting,
            },
            enableSortingRemoval: false,
            enableSorting: true,
            getCoreRowModel: getCoreRowModel(),
            onSortingChange: handleSortingChange as OnChangeFn<SortingState>,
          },
        }}
        slotProps={{
          container: {
            ref: tableContainerRef,
          },
          bodyRow: {
            className: "hover:bg-primary-100 cursor-pointer",
          },
        }}
        noDataMessage='No data found'
      />
      {isChangeStatusDialogOpen && (
        <CancelDialog
          title={`Are you sure you want to ${nextFileStatusAction} this file?`}
          message={`Confirm by clicking on “Yes, ${capitalize(nextFileStatusAction)}” or “Go Back”.`}
          confirmLabel={`Yes, ${capitalize(nextFileStatusAction)}`}
          onClose={onCloseConfirmationDialog}
          onConfirm={handleFileStatusChange}
        />
      )}
    </Box>
  )
}

export default CompanyFilesTable
